import React from 'react'
import { css } from '@emotion/core'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import { useTranslation } from 'react-i18next'
import { ScoreDistribution } from '@/models/survey/statistics'
import { MultilingualText } from '@/models/survey/types'
import { usePickByLanguage } from '@/hooks/use-pick-by-language'

type Props = {
  title: MultilingualText
  distribution: ScoreDistribution
}

const COLORS = ['#0017C1', '#4C5DD3', '#7F8AE0', '#B2B9EC', '#E5E7F9']

/**
 * 円グラフコンポーネント
 * 得点式質問の回答分布を円グラフで表示
 */
export const DonutChartCard = ({ title, distribution }: Props) => {
  const { t } = useTranslation()
  const pickByLanguage = usePickByLanguage()

  const data = Object.entries(distribution)
    .sort(([a], [b]) => Number(b) - Number(a)) // 5 -> 1
    .map(([score, data]) => ({
      name: `${score}: ${getScoreLabel(Number(score), t)}`,
      value: data.percentage,
      count: data.count,
      score: Number(score),
    }))

  return (
    <div css={containerStyle}>
      <div css={titleContainerStyle}>{pickByLanguage(title)}</div>

      {/* 円グラフ */}
      <div css={chartWrapperStyle}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 40, right: 0, bottom: 40, left: 0 }}>
            <Pie data={data} cx="50%" cy="50%" innerRadius={30} outerRadius={55} dataKey="value" startAngle={90} endAngle={-270}>
              {data.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div css={legendStyle}>
        {data.map((item, index) => (
          <div key={index} css={[legendItemStyle, index < data.length - 1 && legendItemBorderStyle]}>
            <div css={legendLeftStyle}>
              <div css={[legendColorStyle, { background: COLORS[index % COLORS.length] }]} />
              <span>
                {item.score}: {getScoreLabel(item.score, t)}
              </span>
            </div>
            <div css={legendRightStyle}>{item.value}%</div>
          </div>
        ))}
      </div>
    </div>
  )
}

const getScoreLabel = (score: number, t: (key: string) => string): string => {
  return t(`SurveyScoreLabel.${score}`) || ''
}

const containerStyle = css({
  width: '100%',
  height: '400px',
  padding: '16px',
  background: '#fff',
  borderRadius: '4px',
  border: '1px solid #D9D9D9',
  marginBottom: '24px',
  display: 'flex',
  flexDirection: 'column',
})

const chartWrapperStyle = css({
  width: '100%',
  height: '160px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

const legendStyle = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: '16px',
})

const legendItemStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: '12px',
  color: '#272727',
  paddingBottom: '8px',
})

const legendItemBorderStyle = css({
  borderBottom: '1px solid #F2F2F2',
  marginBottom: '8px',
})

const legendLeftStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const legendRightStyle = css({
  marginLeft: 'auto', // 右端に押し出す
})

const legendColorStyle = css({
  width: '12px',
  height: '12px',
  borderRadius: '50%',
})

const titleContainerStyle = css({
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#272727',
  textAlign: 'center',
  justifyContent: 'center',
  borderBottom: '1px solid #F2F2F2',
  marginLeft: '-16px',
  marginRight: '-16px',
  paddingBottom: '16px',
})
